import { z } from 'zod';
const text = z.string().trim();
const dateTime = z.string().refine(value => Number.isFinite(Date.parse(value)), 'Invalid voicemail timestamp');
const mediaUrl = z.string().url().refine(value => {
    try {
        const url = new URL(value);
        return (url.protocol === 'https:' || url.protocol === 'http:') && !url.username && !url.password;
    }
    catch {
        return false;
    }
}, 'Invalid voicemail media URL');
/** Canonical native metadata. Presigned URLs are deliberately kept out of storage. */
export const voicemailMetadataSchema = z.object({
    id: text.min(1).max(256),
    displayHeight: z.number().int().positive().optional(),
    displayWidth: z.number().int().positive().optional(),
    fromCallBack: z.enum(['account', 'phone', 'none']).optional(),
    fromName: text.min(1),
    fromPhone: text.max(128).optional(),
    fromRole: z.enum(['client', 'interpreter']).optional(),
    duration: z.number().finite().nonnegative(),
    isRead: z.boolean(),
    timestamp: dateTime,
    transcript: z.string().optional()
});
const voicemailSchema = voicemailMetadataSchema.extend({
    playbackUrl: mediaUrl.optional(),
    playbackUrlExpiresAt: dateTime.optional(),
    thumbnailUrl: mediaUrl.optional()
});
const absent = (value) => value === null || value === '' ? undefined : value;
/** The name shown for a VideoMail with no caller name. It is not a name to call back. */
export const UNKNOWN_VOICEMAIL_CALLER = 'Unknown caller';
/** Accept documented server fields and canonical native metadata, never recipient phone data. */
export function checkedVoicemail(value) {
    const raw = z.record(z.string(), z.unknown()).parse(value);
    const callerName = raw.caller_name ?? raw.fromName;
    return voicemailSchema.parse({
        id: raw.id,
        fromName: callerName == null || (typeof callerName === 'string' && !callerName.trim()) ? UNKNOWN_VOICEMAIL_CALLER : callerName,
        fromPhone: absent(raw.caller_phone ?? raw.fromPhone),
        fromCallBack: absent(raw.caller_callback ?? raw.fromCallBack),
        // The stored shape (session/videomail-shape); null on an older server.
        displayHeight: absent(raw.displayHeight ?? raw.display_height),
        displayWidth: absent(raw.displayWidth ?? raw.display_width),
        fromRole: absent(raw.caller_role ?? raw.fromRole),
        duration: raw.duration_seconds ?? raw.durationSeconds ?? raw.duration ?? 0,
        isRead: raw.seen ?? raw.isRead,
        timestamp: raw.created_at ?? raw.timestamp,
        transcript: absent(raw.transcript),
        playbackUrl: absent(raw.playbackUrl ?? raw.playback_url),
        playbackUrlExpiresAt: absent(raw.playbackUrlExpiresAt),
        thumbnailUrl: absent(raw.thumbnailUrl ?? raw.thumbnail_url)
    });
}
/**
 * The opened message (GET /api/voicemail/messages/:id) in place of its inbox
 * row. The caller fields that the detail does not carry keep the inbox value:
 * before 2026-11-02 the detail sent no caller number, so a message lost its
 * call back once it played (Robert, 2026-11-02). Everything else, the
 * playback URL included, is the detail's.
 *
 * @param {NativeVoicemail} row - The inbox row.
 * @param {NativeVoicemail} opened - The checked detail.
 * @returns {NativeVoicemail}
 */
export function mergeOpened(row, opened) {
    return {
        ...opened,
        displayHeight: opened.displayHeight ?? row.displayHeight,
        displayWidth: opened.displayWidth ?? row.displayWidth,
        fromCallBack: opened.fromCallBack ?? row.fromCallBack,
        fromPhone: opened.fromPhone ?? row.fromPhone,
        fromRole: opened.fromRole ?? row.fromRole
    };
}
export function checkedVoicemailInbox(value) {
    const raw = z.object({ messages: z.array(z.unknown()).max(50), total: z.number().int().nonnegative(),
        unreadCount: z.number().int().nonnegative() }).parse(value);
    const messages = raw.messages.map(checkedVoicemail);
    if (new Set(messages.map(message => message.id)).size !== messages.length
        || raw.total < messages.length || raw.unreadCount > raw.total) {
        throw new Error('Voicemail inbox could not be confirmed.');
    }
    return { messages, total: raw.total, unreadCount: raw.unreadCount };
}
export function confirmVoicemailMutation(value) {
    z.object({ success: z.literal(true) }).parse(value);
}
export function checkedVoicemailCount(value) {
    return z.object({ count: z.number().int().nonnegative() }).parse(value).count;
}
